/**
 * The operator's editor for a host's three capacity knobs: rooms per process, players per room,
 * and the players the host will serve at once.
 *
 * These are not the per-room policy in `SettingsDialog` — they decide the *shape* of the
 * deployment rather than what happens inside one room, and none of them touches a room or a
 * player that is already running. A lower room cap refuses the next room; a lower seat count
 * opens the next room smaller; a lower ceiling refuses the next player.
 *
 * **TWO OF THE THREE ARE BACKSTOPS AND THE DIALOG HAS TO SAY SO.** `maxRooms x maxPlayers` is a
 * product an operator reads as a seat total, and it is deliberately far above what the host was
 * measured to serve — 20 x 100 on an API process measured at ~500 players. This dialog used to
 * print "Measured-safe" under both halves of that product and no ceiling at all, which is a page
 * telling an operator the box is provisioned for two thousand people. So:
 *
 * - **The implied seat total is printed live, against the ceiling**, as the operator types. The
 *   two numbers are shown together or not at all, because either one alone is the misreading.
 * - **The measured range is printed under every field**, from `SAFE_HOST_LIMITS`, which is
 *   the same table the server judges on. An operator should not have to open
 *   `docs/architecture.md` to find out what a number means.
 * - **Leaving that range needs an explicit confirmation.** `hostLimitWarnings` is run here on
 *   what has been typed — merged over what the host reports, so the product is judged too, and
 *   exactly as `admin.updateLimits` runs it on what arrives. The reasons an operator reads before
 *   ticking the box are the reasons the server would have refused with. The check is re-done on
 *   the server; this is the half that explains it.
 *
 * **THE FOURTH KNOB IS NOT A NUMBER.** `locked` closes the host to rooms the matchmaker would
 * invent — an overflow room, or a declared room that names no host — while leaving everything
 * else about it alone: it keeps its rooms, keeps serving them, and still wakes the declarations
 * PINNED to it. It has no measured range to print, so what it gets instead is the sentence
 * saying what it does and the FLEET guard, `hostLockWarnings`, which is the same function the
 * server runs before it accepts: locking the last unlocked host is a decision, not an accident.
 *
 * RESTORE DEFAULTS sits in here rather than on the card behind it, because "the defaults" is a
 * statement about exactly these numbers and it should be read next to them — the lock included,
 * because a restore that left one knob where an operator had dragged it is the button nobody
 * can trust.
 */

import {
  type FleetHostLock,
  ROOM_MAX_PLAYERS,
  hostLimitWarnings,
  hostLockWarnings,
  impliedHostSeats,
} from '@chompdome/shared/constants';
import { hostLimitsPatchSchema } from '@chompdome/shared/schemas';
import type { HostKind, HostLimitKnob, HostLimits, LimitRange } from '@chompdome/shared/types';
import { useEffect, useState } from 'react';
import type { z } from 'zod';
import { NumberField } from '@/components/admin/number-field';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';

export type HostLimitsPatch = z.infer<typeof hostLimitsPatchSchema>;

export interface CapacityDialogProps {
  open: boolean;
  /** The host's id, for the title — the console addresses everything by it. */
  workerId: string;
  hostKind: HostKind;
  /** What the host reports now. The base every diff is taken against. */
  limits: HostLimits;
  /** What RESTORE DEFAULTS would put back, named so the button can say it. */
  measured: HostLimits;
  /**
   * Keyed by the three numeric knobs rather than by `keyof HostLimits`: the lock is a boolean
   * and has no range to draw under a field.
   */
  safe: Record<HostLimitKnob, LimitRange>;
  /**
   * Every host in the deployment and whether it is locked, so the fourth knob can be judged the
   * way the server judges it. Locking is the one change whose cost is not paid by the host being
   * changed: with no unlocked host left, a player the roster cannot seat is told the whole fleet
   * is at capacity. `hostLockWarnings` is that judgement, and it is the same function
   * `admin.updateLimits` runs before it accepts.
   */
  fleet: readonly FleetHostLock[];
  serverError: string | null;
  pending: boolean;
  restorePending: boolean;
  onSubmit: (patch: HostLimitsPatch, confirmUnsafe: boolean) => void;
  onRestore: () => void;
  onClose: () => void;
}

/** Held as typed text so a half-typed field is not silently coerced to 0. See `NumberField`. */
interface Draft {
  maxRooms: string;
  maxPlayers: string;
  maxConcurrentPlayers: string;
  /** The fourth knob, and the only one that is not a number — so it is held as what it is. */
  locked: boolean;
}

function toDraft(limits: HostLimits): Draft {
  return {
    maxRooms: String(limits.maxRooms),
    maxPlayers: String(limits.maxPlayers),
    maxConcurrentPlayers: String(limits.maxConcurrentPlayers),
    locked: limits.locked,
  };
}

/** Blank becomes NaN rather than 0, so the schema rejects it instead of accepting an empty box. */
function toNumber(raw: string): number {
  return raw.trim() === '' ? Number.NaN : Number(raw);
}

function issuesByPath(error: z.ZodError): Record<string, string> {
  const messages: Record<string, string> = {};
  for (const issue of error.issues) messages[issue.path.join('.')] ??= issue.message;
  return messages;
}

function rangeHint(range: LimitRange, unit: string): string {
  return `Measured: ${range.min}–${range.max} ${unit}.`;
}

/** Whole numbers with thousands separators — a seat total is read, not calculated with. */
function count(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * The line that stops the two backstops reading as a capacity plan.
 *
 * Given the *effective* limits — what the host reports, under whatever the operator has typed
 * over it — so a half-typed field shows the host's own number rather than "20 rooms x NaN
 * players". `impliedHostSeats` is the shared multiplication, so the number here is the number
 * `hostLimitWarnings` judged.
 */
function SeatTotal({ limits }: { limits: HostLimits }) {
  const seats = impliedHostSeats(limits);
  const over = seats > limits.maxConcurrentPlayers;
  return (
    <p className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">
        {count(limits.maxRooms)} rooms × {count(limits.maxPlayers)} players = {count(seats)} seats
      </span>{' '}
      — a backstop, not a plan. This host will serve{' '}
      <span className="font-medium text-foreground">
        {count(limits.maxConcurrentPlayers)} concurrent players
      </span>{' '}
      and refuse the next one{over ? ', whatever the product above says' : ''}. The room cap exists
      so a runaway matchmaker cannot pin the process before tick utilization has had a window to
      react.
    </p>
  );
}

export function CapacityDialog({
  open,
  workerId,
  hostKind,
  limits,
  measured,
  safe,
  fleet,
  serverError,
  pending,
  restorePending,
  onSubmit,
  onRestore,
  onClose,
}: CapacityDialogProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(limits));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);

  // Re-seed when the dialog is pointed at another host, and drop the previous attempt's
  // complaints and its confirmation with it — a tick must never carry over to a fresh value.
  useEffect(() => {
    if (!open) return;
    setDraft(toDraft(limits));
    setErrors({});
    setConfirmed(false);
  }, [open, limits]);

  const typed = {
    maxRooms: toNumber(draft.maxRooms),
    maxPlayers: toNumber(draft.maxPlayers),
    maxConcurrentPlayers: toNumber(draft.maxConcurrentPlayers),
  };
  // Only finite numbers are judged: a half-typed field is a field the schema will complain
  // about on submit, not one to warn about the measurements over.
  const patched = {
    ...(Number.isFinite(typed.maxRooms) ? { maxRooms: typed.maxRooms } : {}),
    ...(Number.isFinite(typed.maxPlayers) ? { maxPlayers: typed.maxPlayers } : {}),
    ...(Number.isFinite(typed.maxConcurrentPlayers)
      ? { maxConcurrentPlayers: typed.maxConcurrentPlayers }
      : {}),
  };
  // `limits` under the patch is what the host would be left with, which is what the seat total
  // and the product warning are both about — and it is what the server judges too, from the same
  // function against the same pair.
  const effective: HostLimits = { ...limits, ...patched };
  // One list, because it is one question — "did you mean this?" — and one confirmation is easier
  // to trust than two. The fleet guard only ever has anything to say about *locking*: unlocking
  // cannot corner a deployment, and a host that is already locked is not being locked again.
  const warnings = [
    ...hostLimitWarnings(hostKind, patched, limits),
    ...hostLockWarnings(workerId, draft.locked, fleet),
  ];

  const submit = () => {
    const parsed = hostLimitsPatchSchema.safeParse({ ...typed, locked: draft.locked });
    if (!parsed.success) {
      setErrors(issuesByPath(parsed.error));
      return;
    }

    // Only what changed. An operator who moved one number means that one number.
    const patch: HostLimitsPatch = {};
    if (parsed.data.maxRooms !== limits.maxRooms) patch.maxRooms = parsed.data.maxRooms;
    if (parsed.data.maxPlayers !== limits.maxPlayers) patch.maxPlayers = parsed.data.maxPlayers;
    if (parsed.data.maxConcurrentPlayers !== limits.maxConcurrentPlayers) {
      patch.maxConcurrentPlayers = parsed.data.maxConcurrentPlayers;
    }
    if (draft.locked !== limits.locked) patch.locked = draft.locked;

    setErrors({});
    onSubmit(patch, warnings.length > 0);
  };

  const blocked = warnings.length > 0 && !confirmed;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Capacity on ${workerId}`}
      description="Rooms this process will hold, seats the next room it opens will have, the players it will serve at once, and whether the matchmaker may invent rooms here at all. None of them changes a room or a player that is already running."
      className="max-w-lg"
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            id="capacity-max-rooms"
            label="Rooms per process (backstop)"
            hint={`${rangeHint(safe.maxRooms, 'rooms')} A backstop, not expected occupancy.`}
            value={draft.maxRooms}
            error={errors.maxRooms}
            onChange={(maxRooms) => setDraft((current) => ({ ...current, maxRooms }))}
          />
          <NumberField
            id="capacity-max-players"
            label="Seats per room (backstop)"
            hint={`${rangeHint(safe.maxPlayers, 'players')} Up to ${ROOM_MAX_PLAYERS} permitted, unmeasured.`}
            value={draft.maxPlayers}
            error={errors.maxPlayers}
            onChange={(maxPlayers) => setDraft((current) => ({ ...current, maxPlayers }))}
          />
          <NumberField
            id="capacity-max-concurrent-players"
            label="Concurrent players on this host"
            hint={`${rangeHint(safe.maxConcurrentPlayers, 'players')} The measured one — this host refuses the next seat past it.`}
            value={draft.maxConcurrentPlayers}
            error={errors.maxConcurrentPlayers}
            onChange={(maxConcurrentPlayers) =>
              setDraft((current) => ({ ...current, maxConcurrentPlayers }))
            }
          />
        </div>

        <SeatTotal limits={effective} />

        {/* The fourth knob. Not a number, no measured range, and the only one whose effect is on
            the MATCHMAKER rather than on this host: it stops rooms being invented here and
            changes nothing else about the box. */}
        <div className="space-y-1.5 rounded-lg border border-border p-3">
          <label className="flex items-start gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={draft.locked}
              onChange={(event) =>
                setDraft((current) => ({ ...current, locked: event.target.checked }))
              }
              className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
            />
            Locked — take no room the matchmaker invents
          </label>
          <p className="text-xs text-muted-foreground">
            A locked host keeps the rooms it has, keeps serving the players in them, and still wakes
            the declared rooms PINNED to it. What it refuses is an overflow room, and a declared
            room that names no host. Reserve a box for its own declarations with it.
          </p>
        </div>

        {warnings.length > 0 && (
          <div
            className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3"
            role="alert"
          >
            <p className="text-sm font-semibold text-destructive">
              Outside the range these numbers were measured at
            </p>
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
            <label className="flex items-start gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer accent-destructive"
              />
              Save these values anyway
            </label>
          </div>
        )}

        {serverError && (
          <p className="text-sm text-destructive" role="alert">
            {serverError}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={restorePending}
            onClick={onRestore}
          >
            {restorePending
              ? 'Restoring…'
              : `Restore defaults (${measured.maxRooms} × ${measured.maxPlayers}, ${measured.maxConcurrentPlayers} players)`}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={pending || blocked}>
              {pending ? 'Saving…' : 'Save capacity'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
